import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { BookOpen, Lock, Plus, ImageIcon, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { logActivity } from "@/lib/activity-logger";
import { uploadAndGetSignedUrl, refreshSignedUrls, validateFileSize } from "@/lib/storage";
import VoiceRecorder from "@/components/VoiceRecorder";

export default function BoardMeetingPage() {
  const { user, profile } = useAuth();
  const [reflections, setReflections] = useState<any[]>([]);
  const [isWriting, setIsWriting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState({ wentWell: "", didNotGoWell: "", couldBeBetter: "" });
  const [loading, setLoading] = useState(true);
  const [attachments, setAttachments] = useState<Record<string, any[]>>({});

  const workspaceId = profile?.workspace_id;
  const currentMonth = new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" });

  useEffect(() => {
    if (!workspaceId) return;
    supabase
      .from("board_meetings")
      .select("*")
      .eq("workspace_id", workspaceId)
      .order("submitted_at", { ascending: false })
      .then(({ data, error }) => {
        if (error) toast.error("Couldn't load reflections", { description: error.message });
        setReflections(data || []);
        setLoading(false);
        (data || []).forEach((r: any) => loadAttachments(r.id));
      });
  }, [workspaceId]);

  const loadAttachments = async (meetingId: string) => {
    const { data } = await supabase
      .from("board_meeting_attachments")
      .select("*")
      .eq("meeting_id", meetingId)
      .order("created_at", { ascending: false });
    const withUrls = await refreshSignedUrls(data || [], "file_url");
    setAttachments((prev) => ({ ...prev, [meetingId]: withUrls }));
  };

  

  const submitReflection = async () => {
    if (!draft.wentWell.trim() && !draft.didNotGoWell.trim() && !draft.couldBeBetter.trim()) return;
    if (!workspaceId) return;
    try {
      const { data, error } = await supabase
        .from("board_meetings")
        .insert({
          workspace_id: workspaceId,
          author_id: user!.id,
          month: currentMonth,
          went_well: draft.wentWell.trim() || null,
          did_not_go_well: draft.didNotGoWell.trim() || null,
          could_be_better: draft.couldBeBetter.trim() || null,
          is_locked: false,
          submitted_at: new Date().toISOString(),
        })
        .select()
        .single();
      if (error) {
        toast.error("Couldn't submit", { description: error.message });
        return;
      }
      setReflections([data, ...reflections]);
      setDraft({ wentWell: "", didNotGoWell: "", couldBeBetter: "" });
      setIsWriting(false);
      toast.success("Reflection saved 💛", { description: "You can still edit it until you lock it." });
      logActivity("reflection_created", { meeting_id: data.id, month: currentMonth });

      const displayName = profile?.preferred_name || profile?.full_name || "Your partner";
      supabase.functions.invoke("send-notification-email", {
        body: { type: "board_meeting", workspaceId, actorName: displayName, details: { month: currentMonth } },
      }).catch(console.error);
    } catch (e: any) {
      toast.error("Couldn't submit", { description: e.message });
    }
  };

  const updateReflection = async (id: string) => {
    try {
      const { error } = await supabase.from("board_meetings").update({
        went_well: draft.wentWell.trim() || null,
        did_not_go_well: draft.didNotGoWell.trim() || null,
        could_be_better: draft.couldBeBetter.trim() || null,
      }).eq("id", id);
      if (error) { toast.error("Couldn't save", { description: error.message }); return; }
      setReflections(reflections.map((r) =>
        r.id === id ? { ...r, went_well: draft.wentWell.trim(), did_not_go_well: draft.didNotGoWell.trim(), could_be_better: draft.couldBeBetter.trim() } : r
      ));
      setEditingId(null);
      toast.success("Reflection updated ✨");
    } catch (e: any) {
      toast.error("Couldn't save", { description: e.message });
    }
  };

  const lockReflection = async (id: string) => {
    try {
      const { error } = await supabase.from("board_meetings").update({ is_locked: true }).eq("id", id);
      if (error) { toast.error("Couldn't lock", { description: error.message }); return; }
      setReflections(reflections.map((r) => r.id === id ? { ...r, is_locked: true } : r));
      toast.success("Reflection locked 🔒");
      logActivity("reflection_locked", { meeting_id: id });
    } catch (e: any) {
      toast.error("Couldn't lock", { description: e.message });
    }
  };

  const uploadMeetingMedia = async (meetingId: string, file: File) => {
    if (!workspaceId) return;
    const sizeErr = validateFileSize(file);
    if (sizeErr) { toast.error(sizeErr); return; }
    const ext = file.name.split(".").pop();
    const path = `${workspaceId}/meetings/${meetingId}-${Date.now()}.${ext}`;
    const { signedUrl, error: upErr } = await uploadAndGetSignedUrl("couple-uploads", path, file);
    if (upErr || !signedUrl) {
      toast.error("Upload failed", { description: upErr || "Unknown error" });
      return;
    }
    await supabase.from("board_meeting_attachments").insert({
      meeting_id: meetingId,
      file_url: path,
      file_type: file.type.startsWith("image") ? "image" : file.type.startsWith("audio") ? "audio" : "file",
      uploaded_by: user!.id,
    });
    loadAttachments(meetingId);
    toast.success("Media added");
  };

  const handleVoiceRecorded = async (meetingId: string, url: string) => {
    await supabase.from("board_meeting_attachments").insert({
      meeting_id: meetingId, file_url: url, file_type: "audio", uploaded_by: user!.id,
    });
    loadAttachments(meetingId);
    toast.success("Voice note added 🎙️");
  };

  const startEditing = (r: any) => {
    setEditingId(r.id);
    setDraft({ wentWell: r.went_well || "", didNotGoWell: r.did_not_go_well || "", couldBeBetter: r.could_be_better || "" });
  };

  return (
    <div className="space-y-8 max-w-full">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <h1 className="text-3xl md:text-4xl font-display font-semibold text-foreground">Board Meeting</h1>
        <p className="mt-2 text-muted-foreground text-lg">Our ritual for honest reflection and realignment. We suggest doing this monthly.</p>
      </motion.div>

      {!isWriting && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="bg-sage-light border border-border rounded-xl p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="font-display text-lg font-semibold text-foreground">{currentMonth}</h2>
              <p className="text-sm text-muted-foreground mt-1">Ready to reflect? Start a new reflection whenever you need to.</p>
            </div>
            <button onClick={() => setIsWriting(true)} className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity whitespace-nowrap">
              <BookOpen className="w-4 h-4" /> Begin Reflection
            </button>
          </div>
        </motion.div>
      )}

      {isWriting && (
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="bg-card border border-border rounded-xl p-6 space-y-6">
          <h2 className="font-display text-xl font-semibold text-foreground">{currentMonth} Reflection</h2>
          <p className="text-sm text-muted-foreground -mt-4">Take your time. You can edit until you choose to lock it.</p>
          {[
            { key: "wentWell" as const, label: "What went well?", placeholder: "What moments, wins, or progress felt meaningful?" },
            { key: "didNotGoWell" as const, label: "What didn't go well?", placeholder: "Where did we struggle?" },
            { key: "couldBeBetter" as const, label: "What could be better?", placeholder: "What would we do differently?" },
          ].map((field) => (
            <div key={field.key} className="space-y-2">
              <label className="text-sm font-medium text-foreground">{field.label}</label>
              <textarea value={draft[field.key]} onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })} placeholder={field.placeholder} className="w-full bg-background border border-input rounded-lg p-4 text-sm resize-none h-28 focus:outline-none focus:ring-2 focus:ring-ring/40 leading-relaxed" />
            </div>
          ))}
          <div className="flex gap-2 pt-2">
            <button onClick={submitReflection} className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90">Save Reflection</button>
            <button onClick={() => { setIsWriting(false); setDraft({ wentWell: "", didNotGoWell: "", couldBeBetter: "" }); }} className="px-4 py-2.5 bg-secondary text-secondary-foreground rounded-lg text-sm font-medium">Cancel</button>
          </div>
        </motion.div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading reflections...</span>
        </div>
      ) : (
        <div className="space-y-4">
          <h2 className="font-display text-xl font-semibold text-foreground">Reflections</h2>
          {reflections.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No reflections yet. Begin your first one above.</p>
          ) : (
            reflections.map((r, i) => {
              const isEditing = editingId === r.id;
              const meetingAttachments = attachments[r.id] || [];
              return (
                <motion.div key={r.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }} className="bg-card border border-border rounded-xl p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-lg font-semibold text-foreground">{r.month}</h3>
                    <div className="flex items-center gap-2">
                      {r.is_locked ? (
                        <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Lock className="w-3 h-3" /> Locked</span>
                      ) : (
                        <>
                          {!isEditing && r.author_id === user?.id && (
                            <button onClick={() => startEditing(r)} className="text-xs text-primary hover:underline">Edit</button>
                          )}
                          {r.author_id === user?.id && (
                            <button onClick={() => lockReflection(r.id)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><Lock className="w-3 h-3" /> Lock</button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {isEditing ? (
                    <div className="space-y-4">
                      {[
                        { key: "wentWell" as const, label: "What went well?" },
                        { key: "didNotGoWell" as const, label: "What didn't go well?" },
                        { key: "couldBeBetter" as const, label: "What could be better?" },
                      ].map((field) => (
                        <div key={field.key} className="space-y-1">
                          <label className="text-xs font-medium text-muted-foreground">{field.label}</label>
                          <textarea value={draft[field.key]} onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })} className="w-full bg-background border border-input rounded-lg p-3 text-sm resize-none h-24 focus:outline-none focus:ring-2 focus:ring-ring/40" />
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <button onClick={() => updateReflection(r.id)} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium">Save</button>
                        <button onClick={() => setEditingId(null)} className="px-4 py-2 bg-secondary text-secondary-foreground rounded-lg text-sm">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {r.went_well && (<div><p className="text-xs font-medium text-primary mb-1">What went well</p><p className="text-sm text-foreground leading-relaxed">{r.went_well}</p></div>)}
                      {r.did_not_go_well && (<div><p className="text-xs font-medium text-accent mb-1">What didn't go well</p><p className="text-sm text-foreground leading-relaxed">{r.did_not_go_well}</p></div>)}
                      {r.could_be_better && (<div><p className="text-xs font-medium text-muted-foreground mb-1">What could be better</p><p className="text-sm text-foreground leading-relaxed">{r.could_be_better}</p></div>)}
                    </div>
                  )}
                  {meetingAttachments.length > 0 && (
                    <div className="flex gap-2 flex-wrap pt-2 border-t border-border">
                      {meetingAttachments.map((att: any) => (
                        att.file_type === "audio" ? (
                          <audio key={att.id} controls src={att.file_url} className="h-8 max-w-full" />
                        ) : (
                          <img key={att.id} src={att.file_url} alt="" className="w-20 h-20 rounded-lg object-cover max-w-full" loading="lazy" />
                        )
                      ))}
                    </div>
                  )}
                  {!r.is_locked && r.author_id === user?.id && (
                    <div className="flex gap-2 flex-wrap pt-2 border-t border-border">
                      <label className="flex items-center gap-1.5 px-3 py-2 bg-secondary text-secondary-foreground rounded-lg text-sm cursor-pointer hover:bg-secondary/80">
                        <ImageIcon className="w-3.5 h-3.5" /> Add Image
                        <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadMeetingMedia(r.id, f); }} className="hidden" />
                      </label>
                      <VoiceRecorder workspaceId={workspaceId || ""} onRecorded={(url) => handleVoiceRecorded(r.id, url)} />
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}
                  </p>
                </motion.div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
